// src/api/api.js
import axios from 'axios';

const api = axios.create({
  baseURL: (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api',
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(
  (config) => {
    const userInfo = localStorage.getItem('userInfo');
    if (userInfo) {
      const token = JSON.parse(userInfo).token;
      if (token) config.headers['Authorization'] = `Bearer ${token}`;
    }
    // For FormData uploads the browser must set Content-Type with the multipart boundary.
    // The axios default 'application/json' header would override that and break multer.
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Global session handler — expired tokens (401) and ended employment (403).
// Failures on these paths are the login screen's own business: it renders the
// server's message inline, so the global handler must stay out of the way.
const SILENT_401_PATHS = ['/auth/login', '/auth/register', '/auth/google'];
let redirecting = false;

api.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || '';
    const isSilent = SILENT_401_PATHS.some((p) => url.includes(p));

    // Employment ended (resigned / terminated / deactivated). The backend
    // re-checks this on every request, so the token in this browser is now
    // dead — sign them out rather than leaving them on a page where every
    // call fails. Keyed on the explicit flag, never on 403 alone: plain 403s
    // are ordinary "not allowed to see this" responses and must not log
    // anyone out.
    if (error?.response?.data?.accessEnded && !isSilent && !redirecting) {
      try { localStorage.removeItem('userInfo'); } catch (_) {}
      const msg = error.response.data.message || 'Your portal access has ended.';
      if (!window.location.pathname.startsWith('/login')) {
        redirecting = true;
        setTimeout(() => {
          // eslint-disable-next-line no-alert
          alert(msg);
          window.location.href = '/login';
        }, 0);
      }
      return Promise.reject(error);
    }

    if (status === 401 && !isSilent && !redirecting) {
      redirecting = true;
      try { localStorage.removeItem('userInfo'); } catch (_) {}
      if (!window.location.pathname.startsWith('/login')) {
        setTimeout(() => {
          // eslint-disable-next-line no-alert
          alert('Your session has expired. Please log in again.');
          window.location.href = '/login';
        }, 0);
      }
    }
    return Promise.reject(error);
  }
);

// =============================================================================
// Stale-While-Revalidate GET cache
//
// First visit: fetches from network, stores result.
// Repeat visit (< TTL): returns cached data INSTANTLY, revalidates in bg.
// Repeat visit (> TTL): fetches fresh, updates cache.
// Mutation (POST/PUT/PATCH/DELETE): busts cache entries for that resource.
// Pass { noCache: true } in config to bypass for a specific call.
// =============================================================================
const _cache = new Map(); // url+params → { data, ts }
const CACHE_TTL  = 45_000;  // 45 s — fresh window
const STALE_TTL  = 180_000; // 3 min — serve stale while revalidating

const cacheKey = (url, params) =>
  url + (params ? '?' + new URLSearchParams(params).toString() : '');

const _originalGet = api.get.bind(api);

api.get = (url, config = {}) => {
  if (config.noCache) return _originalGet(url, config);

  const key = cacheKey(url, config.params);
  const cached = _cache.get(key);
  const now = Date.now();

  if (cached) {
    const age = now - cached.ts;

    if (age < CACHE_TTL) {
      // Fresh — return instantly, no network call
      return Promise.resolve({ data: cached.data, _fromCache: true });
    }

    if (age < STALE_TTL) {
      // Stale but usable — serve immediately and revalidate in background
      _originalGet(url, config)
        .then(res => _cache.set(key, { data: res.data, ts: Date.now() }))
        .catch(() => {});
      return Promise.resolve({ data: cached.data, _fromCache: true });
    }
  }

  // Cache miss or expired — fetch and store
  return _originalGet(url, config).then(res => {
    _cache.set(key, { data: res.data, ts: Date.now() });
    return res;
  });
};

// Bust cache for the resource path on any mutation
const bustCacheForPath = (url) => {
  // Extract the resource segment, e.g. '/leads/123' → 'leads'
  const segment = url.replace(/^\//, '').split('/')[0];
  for (const key of _cache.keys()) {
    if (key.startsWith(`/${segment}`) || key.startsWith(segment)) {
      _cache.delete(key);
    }
  }
};

['post', 'put', 'patch', 'delete'].forEach(method => {
  const orig = api[method].bind(api);
  api[method] = (url, ...args) => {
    bustCacheForPath(url);
    return orig(url, ...args);
  };
});

// Expose manual bust for cases where a page needs to force-refresh
api.bustCache = (urlPrefix) => {
  if (!urlPrefix) { _cache.clear(); return; }
  for (const key of _cache.keys()) {
    if (key.includes(urlPrefix)) _cache.delete(key);
  }
};

export default api;
